import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  FormControl,
} from "@mui/material";
import PaymentMethodsModal from "./PaymentMethodsModal";

export default function CreateSaleUI({
  open,
  onClose,
  allProducts,
  productTypes,
  empresa,
  selectedProducts,
  selectedType,
  setSelectedType,
  handleQuantityChange,
  paymentMethods,
  setPaymentMethods,
  discount,
  setDiscount,
  handleSubmit,
  handleReset,
  message,
}) {
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const total = Object.entries(selectedProducts)
    .filter(([_, item]) => item.qty > 0)
    .reduce((acc, [productId, { qty, isDecimal }]) => {
      const product = allProducts.find((p) => p.id === parseInt(productId));
      if (!product) return acc;
      const pricePerUnit = product.sell_method.toLowerCase() === "decimal"
        ? product.price * qty / product.quantity_reference
        : product.price * qty;
      return acc + pricePerUnit;
    }, 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Crear Nueva Venta
        {empresa?.stock && (
          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 2 }}
            onClick={() => window.open("/inventario/stock", "_blank")}
          >
            Consultar Stock
          </Button>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          height: "70vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          {/* Tipos de producto (izquierda) */}
          <Box
            sx={{
              width: "25%",
              borderRight: "1px solid #ccc",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              maxHeight: "44vh",
            }}
          >
            <List>
              {productTypes.map((typeObj) => (
                <ListItemButton
                  key={typeObj.id}
                  selected={selectedType === typeObj.type}
                  onClick={() => setSelectedType(typeObj.type)}
                >
                  <ListItemText primary={typeObj.type} />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Productos filtrados (derecha) */}
          <Box
            sx={{
              width: "75%",
              padding: 1,
              overflowY: "auto",
              maxHeight: "46vh",
            }}
          >
            {allProducts
              .filter((product) => product.product_type?.type === selectedType)
              .map((product) => {
                const isDecimal = product.sell_method?.toLowerCase() === "decimal";
                const displayUnit = isDecimal ? "gr" : "";
                
                return (
                  <Box
                    key={product.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        maxWidth: 250,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {product.name}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 30 }}
                        onClick={() => {
                          const currentQty = selectedProducts[product.id]?.qty || 0;
                          const step = isDecimal ? product.quantity_reference : 1;
                          const newQty = Math.max(currentQty - step, 0);
                          handleQuantityChange(product.id, newQty, isDecimal);
                        }}
                      >
                        −
                      </Button>

                      <TextField
                        type="number"
                        size="small"
                        sx={{ 
                          width: isDecimal ? 100 : 60,
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                        }}
                        value={selectedProducts[product.id]?.qty || ""}
                        onChange={(e) =>
                          handleQuantityChange(product.id, e.target.value, isDecimal)
                        }
                        InputProps={{
                          endAdornment: isDecimal ? <Typography sx={{ ml: 1 }}>{displayUnit}</Typography> : null
                        }}
                      />

                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 30 }}
                        onClick={() => {
                          const currentQty = selectedProducts[product.id]?.qty || 0;
                          const step = isDecimal ? product.quantity_reference : 1;
                          const newQty = currentQty + step;
                          handleQuantityChange(product.id, newQty, isDecimal);
                        }}
                      >
                        +
                      </Button>
                    </Box>
                  </Box>

                )
              })}
          </Box>
        </Box>

        {/* Subtotal */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
            pr: 2,
          }}
        >
          Subtotal: ${total}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Método de pago fijo */}
        <Box>
          <FormControl fullWidth margin="dense">
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setShowPaymentModal(true)}
              sx={{ mt: 1 }}
            >
              {paymentMethods.length > 0
                ? `Seleccionar Métodos de Pago (${paymentMethods.length} añadido${paymentMethods.length > 1 ? "s" : ""})`
                : "Seleccionar Métodos de Pago"}
            </Button>
          </FormControl>
          <PaymentMethodsModal
            open={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            initialMethods={paymentMethods}
            onSave={(methods, extraDiscount) => {
              setPaymentMethods(methods);
              setDiscount(extraDiscount);
            }}
            subtotal={total}
          />
          {discount !== 0 && (
            <Typography sx={{ mt: 1 }} variant="body2">
              {discount < 0 ? "Bonificación Extra" : "Recargo Extra"}: ${Math.abs(discount.toFixed(2))}
            </Typography>
          )}
          {message && (
            <Typography color="error" sx={{ mt: 1 }}>
              {message}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button onClick={handleReset} color="secondary">
          Restablecer
        </Button>
        <Box>
          <Button onClick={onClose} color="primary">
            Cerrar
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ ml: 1 }}
            onClick={handleSubmit}
          >
            Confirmar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
